import { Design } from "../../core/models/entities/Design";
import { DesignStatus } from "../../core/models/entities/enums/DesignStatus";
import {
  Approval,
  ApprovalGroup,
  ApprovalStatus,
} from "../../core/models/entities/Approval";
import {
  BaseLogo,
  getLogoState,
  ImageLogo,
  LogoEditStatus,
  LogoState,
} from "../../core/models/entities/BaseLogo";
import { DataError } from "../../core/errors/DataError";
import { NotApprovedOrderItem } from "../../core/models/entities/ErrorStatus";
import {
  DESIGN_NOT_APPROVED_ERROR_CODE,
  LOGO_NOT_APPROVED_ERROR_CODE,
} from "../../data/models/mappers/mapError";
import { enviroment } from "../../enviroment";

// Flag REACT_APP_DESIGN_ORDERABILITY_GUARD_ENABLED (Zendesk #724425, 15/10):
// the ONE rule for "may this design / logo be put in the cart and ordered",
// shared with the API guard OrderDesignApprovalGuardMode (API PR, same
// rule, fail-closed at PlaceOrder / AddProductsToOrder). Pure functions over
// the fields the list rows already carry - 0 API calls. The callers gate on
// the flag; these helpers do not, so they can be unit-tested as a table.
//
// The rule guards ORDERING only. The multi-select checkbox is never
// disabled by it (Nichlas 16/10, after the first prod day: «vi er stadig
// enige om at vi kan markere flere selvom de ikke er godkendte og sende
// korrektur ud») - the same checkbox drives Send via email (the external
// approval mail), Download PDF, multi-delete and folder drag, which must
// work on AWAITING rows. The guard sits in the bulk "Add to Order (n)"
// (filter + skip toast, disabled when nothing selected is orderable), the
// row / popup button and the API (warn -> enforce).
//
// Why "status done" alone is not enough for a design: the JS upload's
// CreateDesign stores a design as done + vendor Pending (JsUploadFunctions
// 4176-4179), and the "send to customer" deferral keeps approval with the
// vendor Approved + customer Pending - both show "AWAITING · Vendor /
// Customer" in the list and must not be orderable until the vote is in.

export { DESIGN_NOT_APPROVED_ERROR_CODE, LOGO_NOT_APPROVED_ERROR_CODE };

// i18n keys (all 10 locale files).
export const DESIGNS_SKIPPED_NOT_APPROVED_KEY =
  "designListStyle.skippedNotApproved";
export const LOGOS_SKIPPED_NOT_APPROVED_KEY = "logoList.skippedNotApproved";
// Hint on the DISABLED bulk "Add to Order (n)" button when nothing selected
// is orderable (Nichlas 16/10: the checkbox itself must stay free, so the
// dealer can tick AWAITING rows and send a proof to several at once).
export const DESIGNS_NONE_ORDERABLE_KEY = "designListStyle.selectedNotApproved";
export const LOGOS_NONE_ORDERABLE_KEY = "logoList.selectedNotApproved";
export const NOT_APPROVED_NOT_ORDERED_KEY = "orderCreate.notApprovedNotOrdered";
// Same sentence without the ": {{items}}" list, for the edge where the 400
// body named no items and carried no message (never «…ordered: –»).
export const NOT_APPROVED_NOT_ORDERED_GENERIC_KEY =
  "orderCreate.notApprovedNotOrderedGeneric";
// The toast is the only explanation the user gets for a refused order and
// it lists MULs - keep it on screen longer than the default 5 s.
export const NOT_APPROVED_TOAST_MS = 10_000;

function isBlocking(participant: Approval | null | undefined): boolean {
  if (!participant) return false;
  return (
    participant.status === ApprovalStatus.Pending ||
    participant.status === ApprovalStatus.Rejected
  );
}

/** True when no participant of the group is Pending or Rejected (null group = nobody asked = ok). */
export function approvalGroupSettled(
  approval: ApprovalGroup | null | undefined,
): boolean {
  if (!approval) return true;
  return (
    !isBlocking(approval.vendor) &&
    !isBlocking(approval.customer) &&
    !isBlocking(approval.external)
  );
}

/**
 * True when a participant of the group (vendor / customer / external) still
 * has to vote (status Pending). Flag REACT_APP_DESIGN_STATUS_FOLLOWS_APPROVAL_
 * ENABLED: the design list's Status cell shows AWAITING for exactly these
 * rows, whatever design.status says (JS upload = done + vendor Pending,
 * deferred customer approval = approval + customer Pending). Rejected votes
 * are not "pending" - the rejected row keeps its own rendering. Null group =
 * nobody asked = false. Callers gate on the flag.
 */
export function approvalHasPendingParticipant(
  approval: ApprovalGroup | null | undefined,
): boolean {
  if (!approval) return false;
  return [approval.vendor, approval.customer, approval.external].some(
    (participant) => participant?.status === ApprovalStatus.Pending,
  );
}

/**
 * Rule §1 for designs: status done AND no participant (vendor / customer /
 * external) Pending or Rejected. Draft, approval and rejected designs are
 * never orderable; a done design with a pending vote (JS upload, deferred
 * customer approval) is not either.
 */
export function isDesignOrderable(design: Design): boolean {
  if (!design) return false;
  if (design.status !== DesignStatus.Done) return false;
  return approvalGroupSettled(design.approval);
}

/**
 * Rule §1 for logos: only status done is orderable - draft, validating and
 * rejected are refused (the API's LogoExceptionThrower lets exactly those
 * three through to CommitLogo, which is the defect the guard closes). Rows
 * without a status field (text / name / number / symbol logos) fall back to
 * the row button's own rule, getLogoState === Approved, so nothing that is
 * orderable from the row becomes un-orderable in bulk.
 */
export function isLogoOrderable(logo: BaseLogo): boolean {
  if (!logo) return false;
  const status = (logo as ImageLogo).status;
  if (typeof status === "string") {
    return status === LogoEditStatus.Done;
  }
  return getLogoState(logo) === LogoState.Approved;
}

/**
 * Logo ROW button (logo-list-item): getLogoState calls a Validating image
 * logo whose votes are all Approved "Approved" and shows Stepper + "Add to
 * order" - but rule §1 (status done only) refuses it, so the API would 400
 * at checkout. True = the row must not offer "Add to order" (it shows
 * "View designs" instead). Callers gate on the flag.
 */
export function isLogoRowAddToOrderBlocked(logo: BaseLogo): boolean {
  if (!logo) return false;
  return getLogoState(logo) === LogoState.Approved && !isLogoOrderable(logo);
}

export interface OrderableSplit<T> {
  orderable: T[];
  skipped: T[];
}

export function splitOrderableDesigns(designs: Design[]): OrderableSplit<Design> {
  const orderable: Design[] = [];
  const skipped: Design[] = [];
  for (const d of designs) (isDesignOrderable(d) ? orderable : skipped).push(d);
  return { orderable, skipped };
}

export function splitOrderableLogos(logos: BaseLogo[]): OrderableSplit<BaseLogo> {
  const orderable: BaseLogo[] = [];
  const skipped: BaseLogo[] = [];
  for (const l of logos) (isLogoOrderable(l) ? orderable : skipped).push(l);
  return { orderable, skipped };
}

/**
 * True when there IS a selection and not one of its designs is orderable:
 * the bulk "Add to Order (n)" button is then disabled with a hint instead
 * of a click that would only skip everything. Empty selection = false (the
 * button has its own "select first" rule). Callers gate on the flag.
 */
export function noneOrderableDesigns(designs: Design[]): boolean {
  return designs.length > 0 && !designs.some(isDesignOrderable);
}

/** Logo twin of noneOrderableDesigns (rule §1 for logos: status done only). */
export function noneOrderableLogos(logos: BaseLogo[]): boolean {
  return logos.length > 0 && !logos.some(isLogoOrderable);
}

/**
 * The API guard's refusal (mode enforce): 400 { code: "designNotApproved" |
 * "logoNotApproved", items: [{ mul, title, designId | logoId, reason }] } at
 * PlaceOrder / CreateOrder / CommitOrder. errorStatusFromBody keeps the
 * marker as errorCode and the items as notApprovedItems (flag on). Flag off
 * = always false, whatever the error = today's compound toast.
 */
export function isNotApprovedOrderError(error: unknown): boolean {
  if (!enviroment.designOrderabilityGuardEnabled) return false;
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  if (!status || status.code !== 400) return false;
  return (
    status.errorCode === DESIGN_NOT_APPROVED_ERROR_CODE ||
    status.errorCode === LOGO_NOT_APPROVED_ERROR_CODE
  );
}

/** The items the 400 named, de-duplicated by MUL / product no / id; [] when the body carried none. */
export function notApprovedOrderItems(error: unknown): NotApprovedOrderItem[] {
  const status = error instanceof DataError ? error.errorStatus : undefined;
  const items = status?.notApprovedItems ?? [];
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = itemLabel(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// The label the user can find the line by: the design's MUL, else the
// item's product number (the API sets `mul` only for designs and
// `productNo` = Product.Sku ?? Serial for logos - the internal logo id is
// shown nowhere in the UI), else the id as a last resort.
function itemLabel(item: NotApprovedOrderItem): string {
  if (item.mul) return item.mul;
  if (item.productNo) return item.productNo;
  if (item.designId != null) return `design ${item.designId}`;
  if (item.logoId != null) return `logo ${item.logoId}`;
  return "";
}

/**
 * ONE readable line for the toast: «These items are awaiting approval and
 * were not added or ordered: MUL352091 "Højen G6U", MUL352092 "…"». The
 * API's own English `message` is the fallback when it named no items.
 */
export function notApprovedOrderToastText(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const items = notApprovedOrderItems(error).map((item) => {
    const label = itemLabel(item);
    return item.title ? `${label} "${item.title}"` : label;
  });
  if (items.length === 0) {
    const status = error instanceof DataError ? error.errorStatus : undefined;
    const message = status?.message;
    if (typeof message === "string" && message) {
      return t(NOT_APPROVED_NOT_ORDERED_KEY, { items: message });
    }
    return t(NOT_APPROVED_NOT_ORDERED_GENERIC_KEY);
  }
  return t(NOT_APPROVED_NOT_ORDERED_KEY, { items: items.join(", ") });
}
